import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { valueMatchValidator } from '@unleashed/common/validators/value-match.validator';
import { emailAddressValidator } from '@unleashed/common/validators/email-address.validator';
import { phoneNumberValidator } from '@unleashed/common/validators/phone-number.validator';
import { passwordValidator } from '@unleashed/common/validators/password.validator';
import { NgxRandomService } from 'ngx-random';
import { BusyService } from '@unleashed/services/common';
import { SignUpUser } from '@unleashed/models/account';
import { BookingSessionService } from '@unleashed/services/booking';
import { AccountsApiService } from '@unleashed/api/account';

@Component({
  selector: 'ua-signup-form',
  templateUrl: './signup-form.component.html',
  styleUrls: ['./signup-form.component.scss']
})
export class SignupFormComponent implements OnInit {

  signUpForm: FormGroup;
  formDirty = false;
  showPasswordFields = false;
  parkZipCode?: string;
  @Input() showTitle = true;
  @Input() emailAddress = '';
  @Input() firstName = '';
  @Input() lastName = '';
  @Input() phoneNumber = '';
  @Output() signUpClicked = new EventEmitter<SignUpUser>();
  @Output() backClicked = new EventEmitter<void>();
  @Output() existingUserDetected = new EventEmitter<string>();
  username = '';

  constructor(
    private ngxRandom: NgxRandomService,
    private sessionService: BookingSessionService,
    private busyService: BusyService,
    private accountsService: AccountsApiService
  ) {
    this.signUpForm = new FormGroup({
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      emailAddress: new FormControl('', [Validators.required, emailAddressValidator()]),
      phoneNumber: new FormControl('', [Validators.required, phoneNumberValidator()]),
      // TODO revisit password complexity requirements w/ Peter
      password: new FormControl('', [Validators.required, Validators.minLength(8), Validators.maxLength(30), passwordValidator()]),
      confirmPassword: new FormControl('', [Validators.required])
    });
    this.signUpForm.validator = valueMatchValidator(this.signUpForm, 'password', 'confirmPassword');

    this.signUpForm.valueChanges.subscribe(changes => {
      this.username = changes.emailAddress.toLowerCase();
    });
  }

  checkUserExists(): void {
    if (!this.username.includes('@') || !this.username.includes('.')) {
      return;
    }

    this.busyService.set('check-user', true);

    this.accountsService.accountExists(this.username)
      .subscribe((exists: boolean) => {
        this.busyService.set('check-user', false);
        if (exists) {
          this.existingUserDetected.emit(this.username);
        }
      });
  }

  ngOnInit(): void {
    this.signUpForm.patchValue(
      {
        emailAddress: this.emailAddress,
        firstName: this.firstName,
        lastName: this.lastName,
        phoneNumber: this.phoneNumber
      });

    this.sessionService.sessionStore.subscribe(session => this.parkZipCode = session?.park?.address?.zipCode);

    if (this.emailAddress && this.emailAddress !== '')
      this.checkUserExists();
  }

  signUp(): void {
    if (!this.showPasswordFields) {
      const password = this.ngxRandom.generatePasswordString(10);
      this.signUpForm.patchValue({password, confirmPassword: password});
    }
    this.formDirty = true;
    this.signUpForm.controls.confirmPassword.markAsTouched();
    this.signUpForm.controls.password.markAsTouched();
    this.signUpForm.updateValueAndValidity();
    if (!this.signUpForm.valid) {
      return;
    }

    this.signUpClicked.emit({
      firstName: this.signUpForm.value.firstName,
      lastName: this.signUpForm.value.lastName,
      username: this.signUpForm.value.emailAddress,
      phoneNumber: this.signUpForm.value.phoneNumber,
      password: this.signUpForm.value.password,
      zipCode: this.parkZipCode ?? ''
    });
  }

  togglePassword(): void {
    this.showPasswordFields = !this.showPasswordFields;
  }

  goBack(): void {
    this.backClicked.emit();
  }

}
